import type { FC, ReactNode } from 'react'
import type { LegalSection } from '@/components/generals/legal-document'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LegalDocument } from '@/components/generals/legal-document'

const SECTIONS: LegalSection[] = [
    {
        id: 'overview',
        title: 'Overview',
        paragraphs: [
            'Kerning is built on discretion. Designers trust scouts with their careers and companies trust us with roles they have not announced, so we collect as little as we can and share it only when an introduction needs it.',
            'This policy explains what we collect through this website and our service, why, and what you can ask us to do with it.'
        ]
    },
    {
        id: 'what-we-collect',
        title: 'What we collect',
        paragraphs: [
            'We collect what you give us, and a small amount of anonymous information about visits to the site.'
        ],
        list: [
            'From companies: your name, work email, company, website, the role you are hiring for, your stage, timeline and any notes you add.',
            'From scouts: your name, email, current role, a link to your work, the areas you know best, your recommendation and your visibility preference.',
            'From designers: the details you choose to share with a scout or with us for an introduction.',
            'From everyone: the messages you send us, and anonymous counts of pages viewed.'
        ]
    },
    {
        id: 'how-we-use-it',
        title: 'How we use it',
        paragraphs: [
            'We use your information to reply to you, to run searches and scouting relationships, to make the introductions you agree to, and to keep the service working well. We rely on your consent, on steps taken at your request before an agreement, and on our legitimate interest in running the service carefully.'
        ]
    },
    {
        id: 'analytics-and-cookies',
        title: 'Analytics and cookies',
        paragraphs: [
            'We count visits with privacy-friendly analytics that do not use cookies and do not identify you. The site does not set advertising or cross-site tracking cookies.',
            'Emails sent from this site may record whether a message was opened or a link was clicked, so we know our messages arrive.'
        ]
    },
    {
        id: 'sharing',
        title: 'Who we share it with',
        paragraphs: [
            'We never sell personal information. We share it only where an introduction needs it, and only with your agreement: a company sees a designer only after that designer says yes, and scouts see the briefs they are invited to.',
            'Service providers that host the site, store our data and deliver our email process information on our behalf, under contract and only for that purpose.'
        ]
    },
    {
        id: 'retention',
        title: 'How long we keep it',
        paragraphs: [
            'We keep application and search details for as long as a search or scouting relationship is active, and for up to twenty-four months after our last contact, unless the law requires longer. After that we delete them or make them anonymous.'
        ]
    },
    {
        id: 'your-rights',
        title: 'Your rights',
        paragraphs: [
            'Depending on where you live, you can ask to see the information we hold about you, correct it, delete it, restrict how we use it, take a copy of it, or object to how we use it. You can also complain to your local data protection authority.'
        ]
    },
    {
        id: 'security',
        title: 'Security',
        paragraphs: [
            'Information is sent to us over an encrypted connection and stored with providers that protect it to industry standards. Access inside Kerning is limited to the people who need it to run a search.'
        ]
    },
    {
        id: 'contact',
        title: 'Changes and contact',
        paragraphs: [
            'We may update this policy, and the date at the top of the page always shows the latest version. To ask a question or make a request about your information, reply to any email we have sent you and it will reach our team.'
        ]
    }
]

const Privacy: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Privacy Policy'
                description='How Kerning collects, uses and protects the information shared by companies, designers and scouts.'
            />

            <LegalDocument
                title='Privacy Policy'
                intro='We collect as little as we can, share it only when an introduction needs it, and never sell it.'
                updated='1 October 2026'
                sections={SECTIONS}
            />
        </Layout>
    )
}

export default Privacy
